/**
 * CORE-M-03 Negative Keyword Suggestions tab body.
 *
 * Chans's 2026-09-15 rebuild spec §2.3:
 *   Port the existing Search Terms & Negatives review into Management.
 *   Yes / No per row (no multi-select, no "Apply selected"). Every row
 *   carries evidence — spend, window, conversions, one plain reason.
 *
 * Phase C (CORE-M-03.c, 2026-09-18): the wrapper no longer passes through
 * to the standalone <SearchTermsNegativesTab> (multi-select + batch Apply).
 * It renders its own per-row card view built on the shared row-action
 * primitives (G-5 `NegativeYesNoRow`, G-8 `EvidenceRow`, G-6
 * `useResolvedRows`). This is the row-layer restructure the M-03 docstring
 * previously deferred.
 *
 * The standalone `/ads-tools/search-terms` route keeps its existing UI
 * so bookmarks and operator habits still work; the two live side by side
 * until phase D retires the standalone route.
 *
 * Data plane
 *
 *   Same backend as the standalone route:
 *     GET  /google-ads/weekly/search-terms/review
 *     POST /google-ads/weekly/search-terms/negatives/apply
 *   A "Yes" here calls the apply route with exactly ONE selected item
 *   (`confirmed: true` — blast-radius does not fire for single-row applies
 *   in practice, and if it does, we show the toast; the multi-row
 *   confirmation dialog belongs on the batch route, not here).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { TablePagination, usePagination } from '@/components/ui/data-table';
import { EvidenceRow, type RowEvidence } from '@/components/google-ads/EvidenceRow';
import {
  CONVERTED_TITLE,
  convertedCopy,
  formatConversionCount,
  isConvertingRec,
  recConversions,
  withTermConversions,
} from '@/components/google-ads/convertingSearchTerm';
import { AdsSlowLoad } from '@/features/ads/AdsSlowLoad';
import { NegativeYesNoRow } from '@/features/ads/RecommendationActionRow';
import { useResolvedRows } from '@/features/ads/useResolvedRows';
import {
  applySearchTermNegatives,
  getSearchTermNegativeApply,
  isAppServerError,
  isOutcomeUnknown,
  parseApplyInProgress,
  reviewSearchTermsNegatives,
  type ApplySearchTermNegativesResult,
  type NegativeApplyStatus,
  type SearchTermNegativeRecommendation,
  type SearchTermsReviewResult,
} from '@/services/googleAdsWeeklyService';
import {
  NEVER_REACHED_COPY,
  activeApplyCopy,
  appServerErrorCopy,
  outcomeCopy,
} from '@/components/google-ads/negativeApplyCopy';
import {
  APPLY_NOT_FOUND_AFTER_MS,
  APPLY_POLL_INTERVAL_MS,
  newIdempotencyKey,
} from '@/components/google-ads/useNegativeApplyTracker';
import { useTenantStore } from '@/stores/useTenantStore';

interface NegativeKeywordSuggestionsTabProps {
  tenantId?: string | null;
}

const NEG_PAGE_URL_PARAM = 'page';
/**
 * CORE-M-03.c.2 (2026-09-18, Jacob): default page size is 5, not 25.
 * Each Yes on this tab writes a negative keyword through the audit log —
 * 25 rows on one page reads as too many to work through carefully. Five
 * forces the operator to actually finish each page before flipping.
 * Options widen up to 50 for tenants where the volume is high.
 */
const NEG_DEFAULT_PAGE_SIZE = 5;
const NEG_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

function formatUsdMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '$0';
  const dollars = micros / 1_000_000;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: dollars >= 100 ? 0 : 2,
  });
}

function formatConversions(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  // A fraction (data-driven attribution) never shows as "0.0 conv.".
  return `${formatConversionCount(n)} conv.`;
}

function formatWindow(review: SearchTermsReviewResult | undefined): string {
  if (!review?.start_date || !review?.end_date) return 'this cycle';
  return `${review.start_date} to ${review.end_date}`;
}

/**
 * Dev walk 2026-09-24: what this tab checked, and how many search terms it
 * looked at, when nothing needs a negative. The categories are the ones
 * backend search_terms_negatives_service._classify_one() assigns, in order.
 */
function negativesEmptyText(
  review: SearchTermsReviewResult | undefined,
): string {
  const reviewed = review?.search_terms_reviewed ?? 0;
  const window = formatWindow(review);
  const span = window === 'this cycle' ? 'in this cycle' : `from ${window}`;
  if (reviewed === 0) {
    if (review?.zero_looks_like_a_bug) {
      return `The account had impressions ${span}, but no search terms came back, which should not happen. Refresh, and if it stays empty, check the Google Ads connection in Settings, under Integrations, or ask the team.`;
    }
    return `No search terms came back for this account ${span}, so there was nothing to check.`;
  }
  return `Checked ${reviewed} search term${reviewed === 1 ? '' : 's'} ${span} for competitor or other brand names, job seekers, do-it-yourself searches, questions from people not looking to buy, the wrong kind of business, places outside the service area, and low-intent searches. None need a negative keyword.`;
}

function buildEvidence(
  rec: SearchTermNegativeRecommendation,
  review: SearchTermsReviewResult | undefined,
): RowEvidence {
  const reason =
    (rec.reason && rec.reason.trim()) ||
    (rec.evidence && rec.evidence.trim()) ||
    `Suggested negative for "${rec.search_term}" — ${rec.category || 'no category'}.`;
  return {
    spend: formatUsdMicros(rec.metrics.cost_micros),
    window: formatWindow(review),
    // The search term's conversions over every campaign and ad group.
    conversions: formatConversions(recConversions(rec)),
    reason,
  };
}

interface NegRowProps {
  rec: SearchTermNegativeRecommendation;
  review: SearchTermsReviewResult | undefined;
  pending: boolean;
  onYes: (rec: SearchTermNegativeRecommendation) => void;
  onNo: (rec: SearchTermNegativeRecommendation) => void;
}

function NegRow({ rec, review, pending, onYes, onNo }: NegRowProps) {
  const evidence = buildEvidence(rec, review);
  const chips = [
    // A converting search term (out-of-area only since #3048) is shown for
    // the operator to decide on, flagged first so it is read before Yes.
    ...(isConvertingRec(rec)
      ? [
          {
            label: convertedCopy(recConversions(rec)),
            tone: 'warning' as const,
            title: CONVERTED_TITLE,
          },
        ]
      : []),
    // One word of a longer own-brand name ("edelman" of Edelman-Lyon): most
    // likely another business, but it may be the client. Read before Yes.
    ...(rec.brand_collision
      ? [
          {
            label: 'Brand collision, review',
            tone: 'warning' as const,
            title: `Contains "${rec.brand_collision}", part of the client's own name. Check it is another business before excluding it.`,
          },
        ]
      : []),
    { label: rec.match_type || 'BROAD', tone: 'neutral' as const, title: 'Match type' },
    ...(rec.category
      ? [{ label: rec.category, tone: 'neutral' as const, title: 'Category' }]
      : []),
    ...(rec.metrics.campaign_name
      ? [
          {
            label: rec.metrics.campaign_name,
            tone: 'neutral' as const,
            title: 'Campaign',
          },
        ]
      : []),
  ];
  return (
    <div
      className="rounded-md border border-border bg-warm-bg p-4"
      data-testid="neg-row"
      data-recommendation-id={rec.id}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="text-sm text-muted-foreground">
            Search term that fired
          </div>
          <div className="text-base font-semibold">{rec.search_term}</div>
          <div className="text-sm text-muted-foreground">
            Suggested negative:{' '}
            <span className="font-medium text-foreground">
              {rec.negative_keyword}
            </span>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {pending && (
            <Loader2
              className="h-3.5 w-3.5 animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          )}
          <NegativeYesNoRow
            onYes={() => onYes(rec)}
            onNo={() => onNo(rec)}
            pending={pending}
          />
        </div>
      </div>
      <div className="mt-3">
        <EvidenceRow evidence={evidence} additionalChips={chips} />
        {rec.coverage_note ? (
          <p className="text-xs text-muted-foreground" data-testid="coverage-note">
            {rec.coverage_note}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function NegativeKeywordSuggestionsTab({
  tenantId: tenantIdProp,
}: NegativeKeywordSuggestionsTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['management-negative-keywords', tenantId],
    queryFn: () => reviewSearchTermsNegatives({ tenantId: tenantId ?? '' }),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // Filter out anything already applied at the account before we hand
  // rows to useResolvedRows — the disappear semantics of G-6 apply to
  // the operator's *this-session* action, not to server-side state.
  // Conversions are folded per search term first (a term that converted in
  // another campaign is converting on every row), then applied rows leave.
  const openRecs: SearchTermNegativeRecommendation[] = useMemo(() => {
    const raw = withTermConversions(q.data?.recommendations ?? []);
    return raw.filter((r) => r.already_applied !== true);
  }, [q.data]);

  const { visibleRows, resolve, restore } = useResolvedRows(openRecs, {
    getRowId: (r) => r.id,
  });

  const refreshSuggestions = useCallback(() => {
    // Reconcile with the source of truth so a stale cache doesn't
    // resurrect the row on the next refetch.
    queryClient.invalidateQueries({
      queryKey: ['management-negative-keywords', tenantId],
    });
  }, [queryClient, tenantId]);

  /** The server has the final word on a Yes: only 'failed' brings the row back. */
  const finishYes = useCallback(
    (
      rec: SearchTermNegativeRecommendation,
      status: NegativeApplyStatus | undefined,
      result: ApplySearchTermNegativesResult | null | undefined,
    ) => {
      if (status === 'failed') {
        restore(rec.id);
        toast.error(outcomeCopy(status, result));
        return;
      }
      refreshSuggestions();
      if (status === 'partial' || status === 'abandoned') {
        toast.warning(outcomeCopy(status, result));
        return;
      }
      toast.success(`Applied "${rec.negative_keyword}" as a negative.`);
    },
    [refreshSuggestions, restore],
  );

  // Yes clicks whose outcome is unknown: polled by key every 5 s until the
  // ledger says how they ended. The row stays hidden meanwhile.
  const tracked = useRef(new Map<string, { rec: SearchTermNegativeRecommendation; since: number }>());
  const [trackedCount, setTrackedCount] = useState(0);
  const track = useCallback((key: string, rec: SearchTermNegativeRecommendation) => {
    tracked.current.set(key, { rec, since: Date.now() });
    setTrackedCount(tracked.current.size);
  }, []);
  useEffect(() => {
    if (!trackedCount || !tenantId) return undefined;
    let cancelled = false;
    const done = (key: string) => {
      tracked.current.delete(key);
      setTrackedCount(tracked.current.size);
    };
    const pollAll = async () => {
      for (const [key, { rec, since }] of Array.from(tracked.current)) {
        try {
          const record = await getSearchTermNegativeApply(tenantId, key);
          if (cancelled) return;
          if (record.status === 'running') continue;
          done(key);
          finishYes(rec, record.status, record.result);
        } catch (err) {
          if (cancelled) return;
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404 && Date.now() - since > APPLY_NOT_FOUND_AFTER_MS) {
            // The server never heard of it: nothing changed.
            done(key);
            restore(rec.id);
            toast.error(NEVER_REACHED_COPY);
          }
          // Anything else: keep the row hidden and keep asking.
        }
      }
    };
    void pollAll();
    const timer = setInterval(() => void pollAll(), APPLY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [finishYes, restore, tenantId, trackedCount]);

  const applyMutation = useMutation({
    // One apply at a time per client: the server allows one running apply
    // per client, so parallel Yes clicks would 409 each other.
    scope: { id: `neg-apply-${tenantId ?? ''}` },
    mutationFn: async ({ rec, key }: { rec: SearchTermNegativeRecommendation; key: string }) => {
      return applySearchTermNegatives({
        tenantId: tenantId ?? '',
        selected: [
          {
            recommendation_id: rec.id,
            negative_keyword: rec.negative_keyword,
            match_type: rec.match_type,
            category: rec.category || null,
          },
        ],
        // A single-row apply cannot legitimately trip blast-radius; if it
        // ever does, the 409 lands in onError and the row is restored.
        confirmed: true,
        idempotencyKey: key,
      });
    },
    onSuccess: (result, { rec }) => {
      finishYes(rec, result.status ?? 'success', result);
    },
    onError: (err: unknown, { rec, key }) => {
      const running = parseApplyInProgress(err);
      if (running?.same_request) {
        // This very Yes is already running: follow it.
        track(key, rec);
        return;
      }
      if (!running && isOutcomeUnknown(err)) {
        // It may still be writing: keep the row hidden and follow the key.
        track(key, rec);
        return;
      }
      // The server answered and nothing happened (or the request is over):
      // restore the row so the operator can see it again, with the reason.
      restore(rec.id);
      if (running) {
        toast.error(
          `${activeApplyCopy(running.started_at, running.actor)} Try this one again when it finishes.`,
        );
        return;
      }
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data
          ?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : (detail as { message?: string } | undefined)?.message ||
            'Could not apply this negative — the row is still on the list.';
      toast.error(isAppServerError(err) ? appServerErrorCopy(message) : message);
    },
  });

  const handleYes = useCallback(
    (rec: SearchTermNegativeRecommendation) => {
      // Optimistic disappear — the write fires next (after any Yes already
      // running for this client). One key per click.
      resolve(rec.id);
      applyMutation.mutate({ rec, key: newIdempotencyKey() });
    },
    [resolve, applyMutation],
  );

  const handleNo = useCallback(
    (rec: SearchTermNegativeRecommendation) => {
      // No is session-local: the operator is telling Core "not this one
      // right now." No backend call — a refetch brings the row back.
      resolve(rec.id);
    },
    [resolve],
  );

  // Pagination (25/page default) + `?page=N` URL sync, mirroring
  // CORE-M-02.b. Keeps the two Management tabs' pager behavior aligned.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = (() => {
    const raw = searchParams.get(NEG_PAGE_URL_PARAM);
    if (!raw) return 1;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();
  const pagination = usePagination(visibleRows.length, {
    defaultPageSize: NEG_DEFAULT_PAGE_SIZE,
  });
  const { page, pageSize, setPage, setPageSize, pageCount, slice } = pagination;
  useEffect(() => {
    if (urlPage !== page && urlPage >= 1 && urlPage <= pageCount) {
      setPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage, pageCount]);
  const handlePageChange = useCallback(
    (next: number) => {
      setPage(next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next <= 1) p.delete(NEG_PAGE_URL_PARAM);
          else p.set(NEG_PAGE_URL_PARAM, String(next));
          return p;
        },
        { replace: true },
      );
    },
    [setPage, setSearchParams],
  );
  const pageRecs = slice(visibleRows);

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>
            Pick a workspace to see its flagged search terms.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (q.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reading flagged search terms</CardTitle>
          <CardDescription>
            <AdsSlowLoad
              label="Pulling recent search terms and negatives"
              tenantId={tenantId}
            />
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (q.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not read flagged search terms</CardTitle>
          <CardDescription>
            Core could not reach the Search Terms review endpoint for{' '}
            <span className="font-medium text-foreground">{tenantId}</span>.
            Check the Google Ads connection in Settings, under Integrations,
            then reload.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (visibleRows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No negative keywords to add</CardTitle>
          <CardDescription>
            {openRecs.length === 0
              ? negativesEmptyText(q.data)
              : `You have resolved all ${openRecs.length} suggestion${openRecs.length === 1 ? '' : 's'} in this session. Reload the page to see anything that came back on a refetch.`}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Negative Keyword Suggestions</CardTitle>
          <CardDescription>
            Core scanned {q.data?.search_terms_reviewed ?? 0} search terms for{' '}
            {tenantId} between {q.data?.start_date} and {q.data?.end_date}.{' '}
            {q.data?.client_safe_summary}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 text-sm text-muted-foreground">
          Yes adds the suggested negative to the account through the audit
          log; No hides the row for this session (a refresh brings it back).
        </CardContent>
      </Card>

      <div className="space-y-2">
        {pageRecs.map((rec) => (
          <NegRow
            key={rec.id}
            rec={rec}
            review={q.data}
            pending={
              applyMutation.isPending && applyMutation.variables?.rec.id === rec.id
            }
            onYes={handleYes}
            onNo={handleNo}
          />
        ))}
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={visibleRows.length}
        onPageChange={handlePageChange}
        onPageSizeChange={setPageSize}
        pageSizeOptions={NEG_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}

export default NegativeKeywordSuggestionsTab;
